import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';

const MODE_OPTIONS = [
  { key: 'glucose_balance', label: 'Şeker dengesi' },
  { key: 'heart_friendly', label: 'Kalp dostu' },
  { key: 'low_sodium', label: 'Tuz kontrollü' },
  { key: 'light_balanced', label: 'Hafif/dengeli' },
  { key: 'iron_support', label: 'Demir desteği' },
];

const LAB_OPTIONS = [
  { marker: 'hba1c', label: 'HbA1c', unit: '%' },
  { marker: 'fasting_glucose', label: 'Açlık glukozu', unit: 'mg/dL' },
  { marker: 'triglyceride', label: 'Trigliserid', unit: 'mg/dL' },
  { marker: 'ferritin', label: 'Ferritin', unit: 'ng/mL' },
  { marker: 'b12', label: 'B12', unit: 'pg/mL' },
];

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
}

export default function HealthPlusActivationModal({ isOpen, onClose, onProceedToPayment }) {
  const [step, setStep] = useState(1);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState('');
  const [targetMemberKey, setTargetMemberKey] = useState('me');
  const [targetMemberId, setTargetMemberId] = useState(null);
  const [declaredModes, setDeclaredModes] = useState([]);
  const [preferences, setPreferences] = useState({
    lowSugar: false,
    lowSalt: false,
    avoidRefinedCarbs: false,
  });
  const [labs, setLabs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const selectedMember = useMemo(
    () => members.find((m) => m.key === targetMemberKey) || null,
    [members, targetMemberKey]
  );

  const getMemberSubLabel = (member) => {
    if (!member) return '';
    if (member.key === 'me' || member.role === 'user') return 'Ben';
    if (member.key === 'household' || member.role === 'household') return 'Hane geneli';
    if (member.key === 'partner' || member.role === 'partner') return 'Eş/Partner';
    if (member.role === 'child' || String(member.key || '').startsWith('child_')) {
      const idx = Number(String(member.key || '').split('_')[1]);
      return Number.isFinite(idx) ? `Çocuk ${idx + 1}` : 'Çocuk';
    }
    return 'Aile Üyesi';
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setSaveError('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    (async () => {
      try {
        setMembersLoading(true);
        setMembersError('');

        const res = await fetch(`${API_BASE_URL}/api/health/members`, {
          credentials: 'include',
          headers: getAuthHeaders(),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'health_members_get_failed');
        if (!mounted) return;

        const next = Array.isArray(data.members) ? data.members : [];
        setMembers(next);
        if (next.length > 0) {
          setTargetMemberKey(next[0].key || 'me');
          setTargetMemberId(next[0].memberId || null);
        }
      } catch (err) {
        console.error('health members fetch error', err);
        if (mounted) setMembersError('Aile üyeleri yüklenemedi.');
      } finally {
        if (mounted) setMembersLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!selectedMember) return;
    setTargetMemberId(selectedMember.memberId || null);
  }, [selectedMember]);

  const toggleMode = (modeKey) => {
    setDeclaredModes((prev) => (prev.includes(modeKey) ? prev.filter((x) => x !== modeKey) : [...prev, modeKey]));
  };

  const updateLab = (marker, field, value) => {
    setLabs((prev) => {
      const index = prev.findIndex((x) => x.marker === marker);
      if (index === -1) {
        return [
          ...prev,
          {
            marker,
            value: '',
            unit: LAB_OPTIONS.find((o) => o.marker === marker)?.unit || '',
            measuredAt: '',
            [field]: value,
          },
        ];
      }
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const getLab = (marker) => labs.find((x) => x.marker === marker) || null;

  const handleSaveAndProceed = async () => {
    try {
      setSaving(true);
      setSaveError('');

      const profileRes = await fetch(`${API_BASE_URL}/api/health/profile`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          targetMemberKey,
          memberId: targetMemberId,
          declaredModes,
          declaredConditions: [],
          preferences: {
            ...preferences,
            healthPlusEnabled: true,
          },
        }),
      });

      const profileData = await profileRes.json().catch(() => ({}));
      if (!profileRes.ok) throw new Error(profileData.error || 'health_profile_upsert_failed');

      const filledLabs = labs
        .filter((x) => x && String(x.value || '').trim() !== '')
        .map((x) => ({
          marker: x.marker,
          value: Number(x.value),
          unit: x.unit,
          measuredAt: x.measuredAt || undefined,
        }))
        .filter((x) => Number.isFinite(x.value) && x.unit);

      for (const lab of filledLabs) {
        const labRes = await fetch(`${API_BASE_URL}/api/health/labs`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            targetMemberKey,
            memberId: targetMemberId,
            marker: lab.marker,
            value: lab.value,
            unit: lab.unit,
            measuredAt: lab.measuredAt,
            source: 'manual',
          }),
        });

        const labData = await labRes.json().catch(() => ({}));
        if (!labRes.ok) throw new Error(labData.error || 'health_lab_create_failed');
      }

      onProceedToPayment?.({
        targetMemberKey,
        memberId: targetMemberId,
        declaredModes,
      });
    } catch (err) {
      console.error('health activation save error', err);
      setSaveError('Sağlık+ bilgileri kaydedilemedi. Lütfen tekrar dene.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl rounded-[2rem] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#0f1722]">
        <div className="flex items-center justify-between border-b border-slate-100 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-primary/80">Sağlık+ Aktivasyon</p>
            <h3 className="mt-1 text-xl font-black text-slate-900 dark:text-white">Adım {step} / 4</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-slate-100 p-2 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="p-5">
          {step === 1 && (
            <div>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Sağlık+ ayarını hangi aile üyesi için yapmak istiyorsun?</p>
              {membersLoading ? <p className="text-sm text-slate-500">Üyeler yükleniyor...</p> : null}
              {membersError ? <p className="text-sm font-semibold text-red-500">{membersError}</p> : null}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {members.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      setTargetMemberKey(m.key);
                      setTargetMemberId(m.memberId || null);
                    }}
                    className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold ${
                      targetMemberKey === m.key
                        ? 'border-primary bg-primary/10 text-slate-900 dark:text-white'
                        : 'border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {m.label}
                    <div className="mt-1 text-xs font-medium text-slate-500">{getMemberSubLabel(m)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Beslenme modlarını seç. (Birden fazla seçilebilir)</p>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {MODE_OPTIONS.map((mode) => (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => toggleMode(mode.key)}
                    className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold ${
                      declaredModes.includes(mode.key)
                        ? 'border-primary bg-primary/10 text-slate-900 dark:text-white'
                        : 'border-slate-200 text-slate-700 dark:border-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-1 gap-2 md:grid-cols-3">
                {[
                  ['lowSugar', 'Şeker azaltma'],
                  ['lowSalt', 'Tuz hassasiyeti'],
                  ['avoidRefinedCarbs', 'Beyaz un/katkı azaltma'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                    <input
                      type="checkbox"
                      checked={!!preferences[key]}
                      onChange={(e) => setPreferences((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-300">Opsiyonel tahlil değerleri (istersen boş bırakabilirsin).</p>
              <div className="space-y-3">
                {LAB_OPTIONS.map((lab) => {
                  const current = getLab(lab.marker) || {};
                  return (
                    <div key={lab.marker} className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 p-3 md:grid-cols-4 dark:border-slate-700">
                      <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{lab.label}</div>
                      <input
                        className="rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                        placeholder="Değer"
                        value={current.value || ''}
                        onChange={(e) => updateLab(lab.marker, 'value', e.target.value)}
                      />
                      <input
                        className="rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                        placeholder="Birim"
                        value={current.unit || lab.unit}
                        onChange={(e) => updateLab(lab.marker, 'unit', e.target.value)}
                      />
                      <input
                        type="date"
                        className="rounded-xl border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                        value={current.measuredAt || ''}
                        onChange={(e) => updateLab(lab.marker, 'measuredAt', e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h4 className="text-lg font-black text-slate-900 dark:text-white">Ödeme öncesi özet</h4>
              <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                <p>
                  <b>Üye:</b> {selectedMember?.label || getMemberSubLabel(selectedMember) || 'Seçilmedi'}
                </p>
                <p>
                  <b>Seçili modlar:</b> {declaredModes.length > 0 ? declaredModes.join(', ') : 'Yok'}
                </p>
                <p>
                  <b>Not:</b> Ödeme tamamlanmadan Sağlık+ modifier açılmaz.
                </p>
              </div>
              {saveError ? <p className="mt-3 text-sm font-semibold text-red-500">{saveError}</p> : null}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 p-5 dark:border-slate-800">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1 || saving}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold disabled:opacity-50 dark:border-slate-700"
          >
            Geri
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={() => setStep((s) => Math.min(4, s + 1))}
              disabled={saving}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-black text-white dark:bg-white dark:text-slate-900"
            >
              Devam
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSaveAndProceed}
              disabled={saving}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-black text-white disabled:opacity-60"
            >
              {saving ? 'Kaydediliyor...' : 'Ödeme Adımına Geç'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
